import { SubNavigationItemData } from '@beat-venues/common-types';
import { ReactNode, type JSX } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import type { CommonProps } from '../../types';
import { ButtonOrLink } from '../button-or-link';
import { getIconComponent } from '../icon/icon.utils';
import * as icons from '../icons';
import { Text } from '../text';
import { TextLink } from '../text-link';

export interface NavigationItemProps
    extends CommonProps,
        VariantProps<typeof NavigationItemStyles> {
    label: string;
    icon?: ReactNode;
    href?: string;
    items?: SubNavigationItemData[];
}

export const NavigationItemStyles = tv({
    slots: {
        root: 'relative list-none group/submenu',
        submenu:
            'absolute invisible opacity-0 bg-black visited:invisible hover:visible hover:mt-5 hover:block hover:opacity-100 focus:visible focus:mt-5 focus:block focus:opacity-100 group-focus-within/submenu:visible group-focus-within/submenu:mt-5 group-focus-within/submenu:block group-focus-within/submenu:opacity-100 px-2 -left-2',
    },
});

export const NavigationItem = ({
    label,
    icon,
    href,
    items,
    className,
    testId = 'navigation-item',
    ...props
}: NavigationItemProps): JSX.Element => {
    const { root, submenu } = NavigationItemStyles();
    const iconComponent =
        typeof icon === 'string'
            ? getIconComponent(icon, { size: 'xl' })
            : icon;

    const content = (
        <div className="inline-flex flex-col items-center">
            {iconComponent && <span className="mb-1">{iconComponent}</span>}
            <Text className="text-center" size="sm">
                {label}
            </Text>
        </div>
    );

    return (
        <li
            className={root({
                className,
            })}
            data-testid={testId}
            {...props}
        >
            {items && items.length > 0 ? (
                <>
                    <ButtonOrLink className="focus peer">
                        {content}
                    </ButtonOrLink>
                    <ul className={submenu()}>
                        {items?.map((item) => {
                            return (
                                <li
                                    key={item.id}
                                    className="relative whitespace-nowrap pt-4 last:pb-4"
                                >
                                    <TextLink
                                        className="no-underline hover:cursor-pointer"
                                        href={item.href}
                                        icon={
                                            <icons.ChevronRightSolidIcon size="sm" />
                                        }
                                    >
                                        {item.label}
                                    </TextLink>
                                </li>
                            );
                        })}
                    </ul>
                </>
            ) : href ? (
                <ButtonOrLink className="focus inline-block" href={href}>
                    {content}
                </ButtonOrLink>
            ) : (
                <div>{content}</div>
            )}
        </li>
    );
};
